import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
export function DiagnosisPage() {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [area, setArea] = useState('');
  const [isCalculating, setIsCalculating] = useState(true);
  useEffect(() => {
    const storedScore = sessionStorage.getItem('quizScore');
    const storedArea = sessionStorage.getItem('quizArea');
    if (!storedScore) {
      // No quiz data, redirect to quiz
      navigate('/quiz');
      return;
    }
    setScore(Number(storedScore));
    setArea(storedArea || '');
    // Show calculating animation for 2.5s
    const timer = setTimeout(() => {
      setIsCalculating(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, [navigate]);
  const getResultMessage = () => {
    const areaText = area ? ` especialmente en el área de ${area.toLowerCase()}` : '';
    if (score >= 7) {
      return `Tu señal está severamente bloqueada${areaText}. El nivel de interferencia es alto — esto explica por qué, sin importar cuánto reces, medites o trabajes en vos misma, los resultados no llegan. No es tu fe. No es tu esfuerzo. Es estática acumulada que necesita ser limpiada con urgencia.`;
    } else if (score >= 4) {
      return `Hay interferencia moderada en tu campo${areaText}. Estás atrayendo algunas cosas buenas, pero la estática está frenando tu verdadero potencial. Es como si estuvieras transmitiendo en la frecuencia correcta, pero con ruido de fondo que distorsiona la señal. La buena noticia: esto se puede limpiar.`;
    } else {
      return `Tu señal tiene ruido leve${areaText}. Estás cerca de manifestar con facilidad, pero hay bloqueos sutiles que te impiden dar el salto final. Un pequeño ajuste en tu frecuencia puede ser la diferencia entre "casi" y "por fin."`;
    }
  };
  const getLevelLabel = () => {
    if (score >= 7) return 'INTERFERENCIA SEVERA';
    if (score >= 4) return 'INTERFERENCIA MODERADA';
    return 'INTERFERENCIA LEVE';
  };
  const getLevelColor = () => {
    if (score >= 7) return 'text-cta-red';
    if (score >= 4) return 'text-warm-gold';
    return 'text-gold';
  };
  if (isCalculating) {
    return <div className="min-h-screen bg-navy font-sans text-cream flex flex-col items-center justify-center px-4" data-id="element-346">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="flex flex-col items-center text-center" data-id="element-347">
          <div className="w-16 h-16 border-4 border-warm-gold/20 border-t-warm-gold rounded-full animate-spin mb-8" data-id="element-348"></div>
          <h2 className="font-serif text-2xl md:text-3xl text-cream mb-3" data-id="element-349">
            Analizando tus respuestas...
          </h2>
          <p className="text-warm-gold animate-pulse text-lg" data-id="element-350">
            Calculando tu nivel de interferencia
          </p>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen bg-navy font-sans text-cream flex flex-col items-center justify-center px-4 py-12" data-id="element-351">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.6
    }} className="w-full max-w-2xl flex flex-col items-center text-center" data-id="element-352">
        <p className="text-warm-gold text-sm tracking-[0.2em] uppercase mb-6 font-bold" data-id="element-353">
          ANÁLISIS COMPLETADO
        </p>

        {/* Score Card */}
        <div className="bg-navy-light/50 border border-warm-gold/30 rounded-2xl p-8 md:p-12 w-full mb-10 relative overflow-hidden" data-id="element-354">
          {/* Glow effect */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-warm-gold/10 rounded-full blur-[60px] pointer-events-none" data-id="element-355"></div>

          <h2 className="font-serif text-3xl md:text-4xl text-cream mb-2 relative z-10" data-id="element-356">
            Tu Interference Level:
          </h2>

          <div className="flex items-end justify-center mb-2 relative z-10" data-id="element-357">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3,
            duration: 0.5
          }} className="text-7xl md:text-8xl font-serif font-bold text-warm-gold leading-none" data-id="element-358">
              {score}
            </motion.span>
            <span className="text-3xl text-cream/40 mb-2 ml-1" data-id="element-359">/10</span>
          </div>

          <p className={`text-sm font-bold tracking-[0.15em] uppercase mb-6 relative z-10 ${getLevelColor()}`} data-id="element-360">
            {getLevelLabel()}
          </p>

          {/* Gauge Bar */}
          <div className="w-full h-4 bg-navy-dark rounded-full overflow-hidden mb-8 relative z-10 border border-white/5" data-id="element-361">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${score / 10 * 100}%`
          }} transition={{
            duration: 1.5,
            ease: 'easeOut',
            delay: 0.2
          }} className="h-full bg-gradient-to-r from-gold to-cta-red" data-id="element-362"></motion.div>
          </div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.8
        }} className="text-lg md:text-xl text-cream/90 leading-relaxed relative z-10" data-id="element-363">
            {getResultMessage()}
          </motion.p>
        </div>

        {/* CTA — No price, just solution */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.2
      }} className="w-full flex flex-col items-center" data-id="element-364">
          <h3 className="font-serif text-2xl md:text-3xl text-cream mb-3" data-id="element-365">
            Hay una forma de limpiar esta interferencia.
          </h3>
          <p className="text-cream/70 text-lg mb-8 max-w-md" data-id="element-366">
            Un protocolo de 7 días diseñado específicamente para resetear tu
            frecuencia y desbloquear tu señal.
          </p>

          <button onClick={() => navigate('/')} className="w-full md:w-auto inline-flex items-center justify-center px-10 py-5 bg-warm-gold text-navy font-bold text-xl md:text-2xl rounded-full hover:bg-gold transition-colors duration-300 shadow-xl" data-id="element-367">
            Show Me The Solution <ChevronRight className="w-6 h-6 ml-2" data-id="element-368" />
          </button>
        </motion.div>
      </motion.div>
    </div>;
}